// hooks/useVisitTracker.js - 최적화 버전
import { useState, useEffect, useMemo, useCallback } from 'react';
import { db } from '../firebase';
import { getKoreanTimeISO } from '../utils/timeUtils';

// 디바운스 유틸리티 (컴포넌트 외부로 이동)
const debounce = (func, wait) => {
    let timeout;
    return function executedFunction(...args) {
        const later = () => {
            clearTimeout(timeout);
            func(...args);
        };
        clearTimeout(timeout);
        timeout = setTimeout(later, wait);
    };
};

export const useVisitTracker = (deviceId) => {
    const [visitStats, setVisitStats] = useState({
        totalVisits: 0,
        lastVisit: null,
        firstVisit: null,
        sessionsToday: 0,
        avgSessionTime: 0
    });

    // 체험 사용자 lastActiveDate 업데이트 - 즉시 실행 (디바운스 없음)
    const updateDeviceLastActiveDate = async (deviceId, visitTime) => {
        try {
            const { doc, getDoc, updateDoc } = await import('firebase/firestore');

            const deviceRef = doc(db, 'devices', deviceId);
            const deviceDoc = await getDoc(deviceRef);

            if (deviceDoc.exists()) {
                const deviceData = deviceDoc.data();

                if (deviceData.status === 'trial' || deviceData.status === 'active' || deviceData.status === 'pending') {
                    await updateDoc(deviceRef, {
                        lastActiveDate: visitTime,
                        updatedAt: visitTime
                    });
                }
            }
        } catch (error) {
            console.error('체험 사용자 lastActiveDate 업데이트 실패:', error);
            // 에러가 발생해도 메인 방문 기록에는 영향 없도록 함
        }
    };

    // 방문 기록 저장 - 10초 디바운스 적용 (방문 로그와 통계만, lastActiveDate는 제외)
    const recordVisit = useMemo(() => debounce(async () => {
        if (!deviceId) return;

        // 세션 내에서 이미 기록했는지 확인
        const sessionKey = `visit_recorded_${deviceId}`;
        const alreadyRecorded = sessionStorage.getItem(sessionKey);

        if (alreadyRecorded) {
            console.log('이번 세션에서 이미 방문 기록됨');
            return;
        }

        try {
            const { collection, addDoc, doc, getDoc, setDoc, updateDoc } = await import('firebase/firestore');

            const visitTime = getKoreanTimeISO();
            const today = new Date().toDateString();

            // 방문 로그 payload (referralSource 제거)
            const visitData = {
                deviceId,
                visitTime,
                date: today,
                userAgent: navigator.userAgent,
                sessionId: generateSessionId(),
                pageUrl: window.location.href
            };

            // 사용자 통계 업데이트 - 읽기 최소화
            const userStatsRef = doc(db, 'user_stats', deviceId);

            try {
                const userStatsDoc = await getDoc(userStatsRef);

                if (userStatsDoc.exists()) {
                    const currentStats = userStatsDoc.data();
                    const newTotalVisits = (currentStats.totalVisits || 0) + 1;
                    const todayVisits = currentStats.lastVisitDate === today ?
                        (currentStats.sessionsToday || 0) + 1 : 1;

                    // 업데이트만 수행
                    await updateDoc(userStatsRef, {
                        totalVisits: newTotalVisits,
                        lastVisit: visitTime,
                        lastVisitDate: today,
                        sessionsToday: todayVisits,
                        updatedAt: visitTime
                    });

                    setVisitStats({
                        totalVisits: newTotalVisits,
                        lastVisit: visitTime,
                        firstVisit: currentStats.firstVisit,
                        sessionsToday: todayVisits,
                        avgSessionTime: currentStats.avgSessionTime || 0
                    });
                } else {
                    // 첫 방문 - 새 문서 생성
                    const newStats = {
                        deviceId,
                        totalVisits: 1,
                        firstVisit: visitTime,
                        lastVisit: visitTime,
                        lastVisitDate: today,
                        sessionsToday: 1,
                        avgSessionTime: 0,
                        createdAt: visitTime,
                        updatedAt: visitTime
                    };

                    await setDoc(userStatsRef, newStats);
                    setVisitStats(newStats);
                }

                // 방문 로그는 별도로 추가 (batch 처리 고려)
                await addDoc(collection(db, 'visit_logs'), visitData);

                // 세션에 기록 표시
                sessionStorage.setItem(sessionKey, 'true');

                console.log('방문 기록 저장 완료');
            } catch (firestoreError) {
                console.error('Firestore 방문 기록 실패:', firestoreError);
                // Firebase 실패 시 로컬 스토리지에 백업
                const localVisits = JSON.parse(localStorage.getItem('localVisits') || '[]');
                localVisits.push(visitData);
                localStorage.setItem('localVisits', JSON.stringify(localVisits.slice(-100))); // 최대 100개만 보관
            }

        } catch (error) {
            console.error('방문 기록 저장 실패:', error);
        }
    }, 10000), [deviceId]);

    // 세션 종료 기록 - 최적화
    const recordSessionEnd = useCallback(async (sessionDuration) => {
        if (!deviceId || sessionDuration < 10) return; // 10초 이하는 기록하지 않음

        try {
            const { doc, getDoc, updateDoc } = await import('firebase/firestore');

            const userStatsRef = doc(db, 'user_stats', deviceId);
            const userStatsDoc = await getDoc(userStatsRef);

            if (userStatsDoc.exists()) {
                const currentStats = userStatsDoc.data();
                const totalSessions = currentStats.totalVisits || 1;
                const previousAvg = currentStats.avgSessionTime || 0;

                const newAvgSessionTime = Math.round(
                    ((previousAvg * (totalSessions - 1)) + sessionDuration) / totalSessions
                );

                await updateDoc(userStatsRef, {
                    avgSessionTime: newAvgSessionTime,
                    lastSessionDuration: sessionDuration,
                    updatedAt: getKoreanTimeISO()
                });
            }
        } catch (error) {
            console.error('세션 종료 기록 실패:', error);
        }
    }, [deviceId]);

    // 방문 통계 로드 - 캐싱 적용
    const loadVisitStats = useCallback(async () => {
        if (!deviceId) return;

        // 캐시 확인
        const cacheKey = `visit_stats_${deviceId}`;
        const cached = sessionStorage.getItem(cacheKey);

        if (cached) {
            const parsedCache = JSON.parse(cached);
            if (Date.now() - parsedCache.timestamp < 300000) { // 5분 캐시
                setVisitStats(parsedCache.data);
                return;
            }
        }

        try {
            const { doc, getDoc } = await import('firebase/firestore');

            const userStatsRef = doc(db, 'user_stats', deviceId);
            const userStatsDoc = await getDoc(userStatsRef);

            if (userStatsDoc.exists()) {
                const stats = userStatsDoc.data();
                setVisitStats(stats);

                // 캐시 저장
                sessionStorage.setItem(cacheKey, JSON.stringify({
                    data: stats,
                    timestamp: Date.now()
                }));
            }
        } catch (error) {
            console.error('방문 통계 로드 실패:', error);
        }
    }, [deviceId]);

    // 세션 ID 생성
    const generateSessionId = () => {
        return Date.now().toString(36) + Math.random().toString(36).substr(2, 9);
    };

    // lastActiveDate 업데이트를 위한 디바운스 (봇 방지용 짧은 디바운스)
    const updateLastActiveDebounced = useMemo(() => debounce(async () => {
        await updateDeviceLastActiveDate(deviceId, getKoreanTimeISO());
    }, 3000), [deviceId]);

    // 실제 사용자 인터랙션 감지용 디바운스된 업데이트
    const updateLastActiveOnInteraction = useMemo(() => debounce(async () => {
        await updateDeviceLastActiveDate(deviceId, getKoreanTimeISO());
    }, 2000), [deviceId]);

    // 페이지 로드 시 방문 기록 - 최적화
    useEffect(() => {
        if (deviceId) {
            // lastActiveDate는 짧은 디바운스로 업데이트 (봇 방지)
            // 페이지 로드 후 3초 후에 업데이트
            updateLastActiveDebounced();

            // 방문 로그와 통계는 1초 후 실행 (페이지 로드 완료 후)
            const timer = setTimeout(() => {
                recordVisit();
                loadVisitStats();
            }, 1000);

            // 세션 시작 시간 기록
            const sessionStartTime = Date.now();
            
            // 실제 사용자 인터랙션 감지 (클릭, 스크롤, 터치 등)
            const handleUserInteraction = () => {
                // 사용자가 실제로 상호작용하면 즉시 업데이트 (2초 디바운스)
                updateLastActiveOnInteraction();
            };

            // 실제 사용자 인터랙션 이벤트 리스너 추가
            const events = ['click', 'scroll', 'touchstart', 'keydown', 'mousemove'];
            events.forEach(event => {
                document.addEventListener(event, handleUserInteraction, { passive: true, once: false });
            });

            // 페이지 언로드 시 세션 종료 기록 및 lastActiveDate 업데이트
            const handleBeforeUnload = () => {
                // 앱 종료 시: 디바운스 타이머가 있으면 취소하고 즉시 업데이트
                // 하지만 봇 방지를 위해 최소한의 시간(예: 1초)은 지났는지 확인
                const sessionDuration = Math.round((Date.now() - sessionStartTime) / 1000);
                if (sessionDuration >= 1) { // 최소 1초 이상 지났을 때만 업데이트
                    updateDeviceLastActiveDate(deviceId, getKoreanTimeISO());
                }
                
                const sessionDurationMinutes = Math.round((Date.now() - sessionStartTime) / 1000 / 60);
                if (sessionDurationMinutes >= 1) { // 1분 이상만 기록
                    recordSessionEnd(sessionDurationMinutes);
                }
            };

            // Visibility API 사용하여 탭 변경도 감지
            const handleVisibilityChange = () => {
                if (document.visibilityState === 'hidden') {
                    // 탭이 숨겨질 때: 세션이 충분히 길었을 때만 업데이트
                    const sessionDuration = Math.round((Date.now() - sessionStartTime) / 1000);
                    if (sessionDuration >= 3) { // 최소 3초 이상 지났을 때만 업데이트
                        updateDeviceLastActiveDate(deviceId, getKoreanTimeISO());
                    }
                    
                    const sessionDurationMinutes = Math.round((Date.now() - sessionStartTime) / 1000 / 60);
                    if (sessionDurationMinutes >= 1) {
                        recordSessionEnd(sessionDurationMinutes);
                    }
                }
            };

            window.addEventListener('beforeunload', handleBeforeUnload);
            document.addEventListener('visibilitychange', handleVisibilityChange);

            return () => {
                clearTimeout(timer);
                window.removeEventListener('beforeunload', handleBeforeUnload);
                document.removeEventListener('visibilitychange', handleVisibilityChange);
                // 이벤트 리스너 제거
                events.forEach(event => {
                    document.removeEventListener(event, handleUserInteraction);
                });
                handleBeforeUnload(); // 컴포넌트 언마운트 시에도 호출
            };
        }
    }, [deviceId, recordVisit, loadVisitStats, updateLastActiveDebounced, updateLastActiveOnInteraction, recordSessionEnd]);

    return {
        visitStats,
        recordVisit,
        loadVisitStats
    };
};
